import { useState } from 'react';
import useOrganizationStore from '../store/organizationStore';
import useAuthStore from '../store/authStore';
import AcquireOSLogo from '../components/AcquireOSLogo';
import '../styles/organization.css';

const STEPS = [
  { id: 1, label: 'Company Info',  icon: '🏢' },
  { id: 2, label: 'Contact Details', icon: '📞' },
  { id: 3, label: 'Email Setup',   icon: '✉️' },
];

function StepProgress({ currentStep }) {
  return (
    <div className="org-setup-flow-steps">
      {STEPS.map((step, idx) => {
        const status =
          step.id < currentStep ? 'completed' :
          step.id === currentStep ? 'active' : '';
        return (
          <div key={step.id} className={`org-step-dot ${status}`}>
            <div className="org-step-circle">
              {step.id < currentStep ? '✓' : step.id}
            </div>
            <span className="org-step-label">{step.label}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── Step 1: Company Info ──────────────────────────── */
function Step1({ data, onChange }) {
  return (
    <>
      <h3 className="org-step-heading">Tell us about your company</h3>
      <p className="org-step-desc">
        The AI will use this information to personalize every email, document, and outreach template it creates for you.
      </p>
      <div className="org-form-grid">
        <div className="org-field full-width">
          <label className="org-label">Company Name <span>*</span></label>
          <input
            className="org-input"
            placeholder="e.g. Apex Capital Partners"
            value={data.companyName}
            onChange={e => onChange('companyName', e.target.value)}
          />
        </div>
        <div className="org-field full-width">
          <label className="org-label">Website <span>(optional)</span></label>
          <input
            className="org-input"
            placeholder="e.g. https://apexcapital.com"
            value={data.website}
            onChange={e => onChange('website', e.target.value)}
          />
        </div>
        <div className="org-field full-width">
          <label className="org-label">Company Description <span>(optional)</span></label>
          <textarea
            className="org-textarea"
            placeholder="Brief description of your firm — what you invest in, your focus markets, your strategy…"
            value={data.companyDescription}
            onChange={e => onChange('companyDescription', e.target.value)}
          />
        </div>
      </div>
    </>
  );
}

/* ── Step 2: Contact Details ───────────────────────── */
function Step2({ data, onChange }) {
  return (
    <>
      <h3 className="org-step-heading">Contact details</h3>
      <p className="org-step-desc">
        Contact details are auto-filled in every email footer and outreach template generated by the AI.
      </p>
      <div className="org-form-grid">
        <div className="org-field">
          <label className="org-label">Contact Email</label>
          <input
            className="org-input"
            type="email"
            placeholder="deals@yourcompany.com"
            value={data.contactEmail}
            onChange={e => onChange('contactEmail', e.target.value)}
          />
        </div>
        <div className="org-field">
          <label className="org-label">Phone Number</label>
          <input
            className="org-input"
            type="tel"
            placeholder="(512) 555-0100"
            value={data.phone}
            onChange={e => onChange('phone', e.target.value)}
          />
        </div>
        <div className="org-field full-width">
          <label className="org-label">Office Address <span>(optional)</span></label>
          <input
            className="org-input"
            placeholder="123 Capital Blvd, Suite 400, Austin, TX 78701"
            value={data.officeAddress}
            onChange={e => onChange('officeAddress', e.target.value)}
          />
        </div>
      </div>
    </>
  );
}

/* ── Step 3: Email Signature Setup ─────────────────── */
function Step3({ data, onChange }) {
  // Auto-build signature preview from fields
  const autoSig = [
    data.senderName,
    data.companyName,
    data.phone,
    data.contactEmail,
    data.website,
    data.officeAddress,
  ].filter(Boolean).join('\n');

  const previewSig = data.emailSignature || autoSig;

  return (
    <>
      <h3 className="org-step-heading">Set up your email signature</h3>
      <p className="org-step-desc">
        The AI pre-builds a signature from your info. You can customize it below — it will appear in every email, LOI, and outreach sent through AcquireOS.
      </p>
      <div className="org-form-grid single-col">
        <div className="org-field">
          <label className="org-label">Investor name</label>
          <input
            className="org-input"
            placeholder="e.g. Marcus Reed"
            value={data.senderName}
            onChange={e => onChange('senderName', e.target.value)}
          />
        </div>
        <div className="org-field">
          <label className="org-label">
            Investor Signature <span>(leave blank to auto-generate)</span>
          </label>
          <textarea
            className="org-textarea"
            style={{ minHeight: 110 }}
            placeholder={`Auto-generated from your info:\n\n${autoSig || 'Marcus Reed\nApex Capital Partners\n(512) 555-0100'}`}
            value={data.emailSignature}
            onChange={e => onChange('emailSignature', e.target.value)}
          />
        </div>
      </div>

      {/* Live signature preview */}
      <div className="org-signature-preview">
        <div className="org-signature-preview-label">Investor Signature Preview</div>
        <hr className="org-signature-preview-divider" />
        <div
          className="org-signature-preview-body"
          style={{ borderLeft: `3px solid ${data.brandColor}`, paddingLeft: 12 }}
        >
          {previewSig || '(Your signature will appear here)'}
        </div>
      </div>
    </>
  );
}

/* ── Main Wizard ─────────────────────────────────────────────────────────── */
export default function OrganizationSetupPage({ onComplete }) {
  const { saveProfile } = useOrganizationStore();
  const { sendRegistrationOTP, verifyRegistrationOTP, isLoading } = useAuthStore();

  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [registrationDone, setRegistrationDone] = useState(false);
  const [regError, setRegError] = useState('');
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpError, setOtpError] = useState('');
  const [data, setData] = useState({
    companyName: '',
    website: '',
    logoUrl: '',
    brandColor: '#1B3A6B',
    contactEmail: '',
    phone: '',
    officeAddress: '',
    senderName: '',
    emailSignature: '',
    companyDescription: '',
    communicationTone: 'professional',
  });

  const handleChange = (field, value) => {
    setData(prev => ({ ...prev, [field]: value }));
  };

  const isStep1Valid = Boolean(data.companyName.trim());

  const handleNext = () => {
    if (step < 3) {
      setStep(s => s + 1);
    } else {
      handleFinish();
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(s => s - 1);
  };

  const handleFinish = async () => {
    setSaving(true);
    setRegError('');
    try {
      saveProfile(data);

      if (data.contactEmail && data.contactEmail.trim()) {
        const result = await sendRegistrationOTP({
          name: data.senderName || data.companyName,
          email: data.contactEmail.trim(),
          company: data.companyName,
          role: 'investor',
          userType: 'organization',
          orgName: data.companyName,
          orgUrl: data.website,
          orgDetails: data.companyDescription
        });
        if (!result.success) {
          setRegError(result.error || 'Registration failed. Please try again.');
          setSaving(false);
          return;
        }
        setIsVerifyingOtp(true);
      } else {
        onComplete && onComplete(data);
      }
    } finally {
      setSaving(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (!otpCode.trim() || otpCode.length !== 6) {
      setOtpError('Please enter a 6-digit verification code');
      return;
    }
    setOtpError('');
    const result = await verifyRegistrationOTP(otpCode);
    if (!result.success) {
      setOtpError(result.error);
      return;
    }
    setIsVerifyingOtp(false);
    setRegistrationDone(true);
  };

  const handleSkip = () => {
    saveProfile(data);
    onComplete && onComplete(data);
  };

  if (isVerifyingOtp) {
    return (
      <div className="org-setup-overlay">
        <div className="org-setup-card" style={{ maxWidth: 440 }}>
          <div className="org-setup-logo-row" style={{ justifyContent: 'center', marginBottom: '16px' }}>
            <AcquireOSLogo size={64} />
          </div>
          <h1 className="org-setup-title" style={{ textAlign: 'center', fontSize: '24px', marginBottom: '12px' }}>
            Verify Your Email
          </h1>
          <p className="org-setup-subtitle" style={{ fontSize: '14px', lineHeight: '1.6', marginBottom: '24px', textAlign: 'center' }}>
            We've sent a 6-digit verification code to <strong>{data.contactEmail}</strong>. Please enter the code below to complete your organization registration.
          </p>

          {otpError && (
            <div style={{ margin: '0 0 16px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: 8, color: '#dc2626', fontSize: 13, textAlign: 'center' }}>
              {otpError}
            </div>
          )}

          <form onSubmit={handleVerifyOtp} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div className="org-field">
              <label className="org-label" style={{ textAlign: 'center', display: 'block', width: '100%' }}>Verification Code</label>
              <input
                className="org-input"
                type="text"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                style={{ textAlign: 'center', fontSize: '20px', letterSpacing: '4px' }}
                autoFocus
              />
            </div>

            <button className="org-btn-next" type="submit" style={{ width: '100%' }} disabled={saving || isLoading || otpCode.length !== 6}>
              {saving || isLoading ? 'Verifying...' : 'Verify & Submit'}
            </button>
          </form>

          <button 
            className="org-btn-back" 
            onClick={() => setIsVerifyingOtp(false)}
            style={{ marginTop: '20px', width: '100%' }}
          >
            ← Back to Setup
          </button>
        </div>
      </div>
    );
  }


  if (registrationDone) {
    return (
      <div className="org-setup-overlay">
        <div className="org-setup-card" style={{ textAlign: 'center', padding: '50px 40px' }}>
          <div style={{ fontSize: '56px', marginBottom: '20px' }}>⏳</div>
          <h1 className="org-setup-title" style={{ fontSize: '26px', marginBottom: '14px' }}>
            Registration Submitted!
          </h1>
          <p className="org-setup-subtitle" style={{ fontSize: '15px', lineHeight: '1.7', marginBottom: '32px' }}>
            Thank you for registering <strong>{data.companyName || 'your organization'}</strong>
            {data.contactEmail ? <> (<em>{data.contactEmail}</em>)</> : ''}.
            Your account request is now pending review by our platform administrators.
            <br /><br />
            You will receive a notification email at{' '}
            <strong>{data.contactEmail || 'your registered email'}</strong>{' '}
            once your account status is updated.
          </p>
          <button
            className="org-btn-next"
            onClick={() => onComplete && onComplete(data)}
            style={{ minWidth: 200 }}
          >
            → Return to Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="org-setup-overlay">
      <div className="org-setup-card">
        {/* Header */}
        <div className="org-setup-header">
          <div className="org-setup-logo-row">
            <AcquireOSLogo size={64} onDark />
            <span className="org-setup-badge">ORGANIZATION SETUP</span>
          </div>
          <h1 className="org-setup-title">Set up your organization</h1>
          <p className="org-setup-subtitle">
            The AI uses this to personalize every email, document, and follow-up — making them look like they came directly from your firm.
          </p>
          <StepProgress currentStep={step} />
        </div>

        {/* Body */}
        <div className="org-setup-body">
          {step === 1 && <Step1 data={data} onChange={handleChange} />}
          {step === 2 && <Step2 data={data} onChange={handleChange} />}
          {step === 3 && <Step3 data={data} onChange={handleChange} />}
        </div>

        {regError && (
          <div style={{ margin: '0 32px', padding: '10px 14px', background: '#fef2f2', border: '1px solid #fee2e2', borderRadius: 8, color: '#dc2626', fontSize: 13 }}>
            {regError}
          </div>
        )}

        {/* Footer */}
        <div className="org-setup-footer">
          <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
            {step > 1 && (
              <button className="org-btn-back" onClick={handleBack}>← Back</button>
            )}
            <button className="org-btn-skip" onClick={handleSkip}>
              Skip → Go to Login
            </button>
          </div>

          <button
            className="org-btn-next"
            onClick={handleNext}
            disabled={(step === 1 && !isStep1Valid) || saving || isLoading}
          >
            {saving || isLoading ? (
              <>⟳ Saving...</>
            ) : step < 3 ? (
              <>Next: {STEPS[step].label} →</>
            ) : (
              <>✓ Finish Setup</>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
